import { BROADSIGN_UPDATE_CAMPAIGN_BY_DEAL } from './broadSignUpdateCampaignByDeal'
import { UpdateCampaignByDealModel } from '../../pages/Campaign/CampaignTable/BroadSignAddModal/models/update-campaign-by-deal.model'
import { BroadSignFiltersModel } from './models/broad-sign-filters.model'
import { FetchResult } from '@apollo/client'
import { BroadSignCreationsResponseModel } from './models/broad-sign-creations-response.model'
import { BROADSIGN_CREATIONS } from './broadSignCreations'
import { client } from '../../components/ApolloProvider'
import { BroadSignUpdateCampaignByDealResponse } from '../../types/various'
import { BROADSIGN_CREATIONS_PER_PAGE } from '../../constant'
import { BROADSIGN_ACCEPT_CREATION } from './broadSignAcceptCreation'
import { BroadSignCreationModel } from './models/broad-sign-creation.model'
import { BROADSIGN_REJECT_CREATION } from './broadSignRejectCreation'
import { UPDATE_BROADSIGN_CREATION } from './updateBroadsignCreation'
import { BROADSIGN_CREATION_MEDIA_TREE } from './broadsignCreationMediaTree'
import {
  BroadSignCreationMediumUsage,
  BroadSignMediaTreeAgglomeration,
} from './models/broad-sign-media-tree.model'
import { ASSIGN_BROADSIGN_CREATION_MEDIA } from './assignBroadsignCreationMedia'
import { SEND_BROADSIGN_CREATION_TO_ACCEPTANCE } from './sendBroadsignCreationToAcceptance'
import { BROADSIGN_CREATION_ACCEPTANCES } from './broadsignCreationAcceptances'
import { BroadSignCreationAcceptanceModel } from './models/broad-sign-creation-acceptance.model'

const broadSignApi = {
  broadSignUpdateCampaignByDeal: ({
    dealId,
    campaignId,
  }: UpdateCampaignByDealModel): Promise<
    FetchResult<{ broadsignUpdateCampaignByDeal: BroadSignUpdateCampaignByDealResponse }>
  > =>
    client.mutate({
      mutation: BROADSIGN_UPDATE_CAMPAIGN_BY_DEAL,
      variables: {
        dealId,
        campaignId,
      },
    }),

  fetchBroadSignCreations: (
    filters: BroadSignFiltersModel,
    first: number
  ): Promise<FetchResult<{ broadsignCreations: BroadSignCreationsResponseModel }>> => {
    const { statuses, mediaFormats, dateFrom, dateTo, text } = filters
    const mappedFilters = {
      statuses,
      mediaFormats,
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      ...(text && { text }),
    }

    return client.query({
      query: BROADSIGN_CREATIONS,
      variables: {
        filters: mappedFilters,
        first,
        last: BROADSIGN_CREATIONS_PER_PAGE,
      },
    })
  },

  fetchBroadsignCreationMediaTree: (
    id: string
  ): Promise<FetchResult<{ broadsignCreationMediaTree: BroadSignMediaTreeAgglomeration[] }>> => {
    return client.query({
      query: BROADSIGN_CREATION_MEDIA_TREE,
      variables: { id },
    })
  },

  fetchCreationAcceptances: (
    broadsignCreationId: string
  ): Promise<FetchResult<{ broadsignCreationAcceptances: BroadSignCreationAcceptanceModel[] }>> => {
    return client.query({
      query: BROADSIGN_CREATION_ACCEPTANCES,
      variables: { broadsignCreationId },
      fetchPolicy: 'network-only',
    })
  },

  assignCreationMedia: (
    id: string,
    media: BroadSignCreationMediumUsage[]
  ): Promise<
    FetchResult<{
      assignBroadsignCreationMedia: {
        broadsignCreation: { id: string; media: { asId: string }[] }
        errors: string[]
        success: boolean
      }
    }>
  > => {
    return client.mutate({
      mutation: ASSIGN_BROADSIGN_CREATION_MEDIA,
      variables: { id, media },
    })
  },

  sendCreationToAcceptance: (
    id: string
  ): Promise<
    FetchResult<{
      sendBroadsignCreationToAcceptance: {
        broadsignCreationBuildings: {
          id: string
          buildingId: string
          acceptanceStatus: string
          used: boolean
        }[]
        errors: string[]
        success: boolean
      }
    }>
  > => {
    return client.mutate({
      mutation: SEND_BROADSIGN_CREATION_TO_ACCEPTANCE,
      variables: { id },
    })
  },

  acceptCreation: (
    id: string
  ): Promise<
    FetchResult<{
      broadsignAcceptCreation: { broadsignCreation: Pick<BroadSignCreationModel, 'id' | 'status'> }
    }>
  > => {
    return client.mutate({
      mutation: BROADSIGN_ACCEPT_CREATION,
      variables: { id },
    })
  },

  rejectCreation: (
    id: string,
    rejectionReason: string
  ): Promise<
    FetchResult<{
      broadsignRejectCreation: { broadsignCreation: Pick<BroadSignCreationModel, 'id' | 'status'> }
    }>
  > => {
    return client.mutate({
      mutation: BROADSIGN_REJECT_CREATION,
      variables: { id, rejectionReason },
    })
  },

  updateBroadsignCreation: (
    id: string,
    commercialSupervisorId?: string,
    departmentId?: string,
    advertiserId?: string
  ): Promise<
    FetchResult<{
      updateBroadsignCreation: {
        broadsignCreation: {
          id: string
          commercialSupervisor: { id: string }
          department: { id: string }
          advertiser: { id: string }
        }
        errors: string[]
      }
    }>
  > => {
    return client.mutate({
      mutation: UPDATE_BROADSIGN_CREATION,
      variables: {
        id,
        commercialSupervisorId,
        departmentId,
        advertiserId,
      },
    })
  },
}

export default broadSignApi
